import { FiltersType, ListingRequest } from '@/api/types';
import { client } from '@/client';
import { Course } from '@/client/courses';
import React, { HTMLAttributes, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyLoadingSelect } from '../generic/LazyLoadingSelect';

type CoursesSelectInputProps = {
  onChange: (value: Course | null) => void;
  additionalFilters?: FiltersType;
  selectedValue?: Course | null;
  accountId?: string;
  isSystemAccount?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
} & Omit<HTMLAttributes<HTMLSelectElement>, 'onChange' | 'defaultValue'>;

export const CoursesSelectInput: React.FC<CoursesSelectInputProps> = ({
  onChange,
  className,
  additionalFilters,
  selectedValue,
  accountId,
  isSystemAccount,
  isDisabled,
  placeholder,
  ...rest
}) => {
  const { t } = useTranslation();

  const fetchCourses = useMemo(
    () =>
      isDisabled
        ? undefined
        : async (params: ListingRequest & { search?: string }) =>
            accountId && !isSystemAccount
              ? await client.courses.getAccountCourses(params, accountId)
              : await client.courses.getCourses(params),
    [isDisabled, accountId, isSystemAccount],
  );

  return (
    <LazyLoadingSelect
      fetchService={fetchCourses}
      filter
      useSearch
      optionLabel="name"
      dataKey="id"
      placeholder={placeholder ?? t('courses.select')}
      filterPlaceholder={t('course.search')}
      additionalFilters={additionalFilters}
      selectedValue={selectedValue}
      className={className}
      handleOnChange={onChange}
      isDisabled={isDisabled}
      showClear
      {...(rest as any)}
    />
  );
};
